import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAlert } from '../context/AlertContext';

// `apiBase` lets the same modal serve both entity families:
// `${API}/incidents` (default — unchanged legacy behavior) or
// `${API}/observations` when opened from an observation detail page.
const FiveWhyAnalysis = ({ hazardId, onClose, apiBase = `${API}/incidents` }) => {
  const { showSuccess, showError } = useAlert();
  const [problemStatement, setProblemStatement] = useState('');
  const [whys, setWhys] = useState(['', '', '', '', '']); // Start with 5 whys
  const [rootCause, setRootCause] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchAnalysis();
  }, [hazardId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiBase}/${hazardId}/rca/five-why`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.whys) {
        setProblemStatement(response.data.problem_statement || '');
        const rawWhys = response.data.whys || [];
        const parsed = rawWhys.map(w =>
          typeof w === 'object' && w !== null ? (w.answer || w.question || '') : String(w || '')
        );
        setWhys(parsed.length > 0 ? parsed : ['', '', '', '', '']);
        setRootCause(response.data.root_cause || '');
      }
    } catch (err) {
      console.error('Failed to load analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWhyChange = (index, value) => {
    const newWhys = [...whys];
    newWhys[index] = value;
    setWhys(newWhys);
  };

  const addWhy = () => {
    setWhys([...whys, '']);
  };

  const removeWhy = (index) => {
    if (whys.length > 5) {
      const newWhys = whys.filter((_, i) => i !== index);
      setWhys(newWhys);
    }
  };

  const handleSave = async () => {
    if (!problemStatement.trim()) {
      showError('Please enter a problem statement');
      return;
    }

    setSaveLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/${hazardId}/rca/five-why`,
        {
          hazard_id: hazardId,
          problem_statement: problemStatement,
          whys: whys.filter(w => w.trim() !== ''),
          root_cause: rootCause,
          company_id: '',
          created_by: '',
          created_at: new Date().toISOString()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      showSuccess('5-Why analysis saved successfully!');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save analysis');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1B5C94]">5-Why Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">Identify the root cause by asking "Why?" repeatedly</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Problem Statement */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Problem Statement <span className="text-red-500">*</span>
        </label>
        <textarea
          value={problemStatement}
          onChange={(e) => setProblemStatement(e.target.value)}
          rows="2"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
          placeholder="Clearly state the problem you are investigating..."
        />
      </div>

      {/* Why Questions */}
      <div className="space-y-4 mb-6">
        {whys.map((why, index) => (
          <div key={index} className="relative">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Why #{index + 1}?
            </label>
            <div className="flex gap-2">
              <textarea
                value={why}
                onChange={(e) => handleWhyChange(index, e.target.value)}
                rows="2"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                placeholder={`Ask why the ${index === 0 ? 'problem' : 'previous answer'} occurred...`}
              />
              {index >= 5 && (
                <button
                  onClick={() => removeWhy(index)}
                  className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                  title="Remove this Why"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add More Button */}
      <button
        onClick={addWhy}
        className="mb-6 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-semibold flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Add Another "Why?"
      </button>

      {/* Root Cause */}
      <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Identified Root Cause
        </label>
        <textarea
          value={rootCause}
          onChange={(e) => setRootCause(e.target.value)}
          rows="3"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Based on your analysis, what is the fundamental root cause?"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex-1 bg-[#1B5C94] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#164A7B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saveLoading ? 'Saving...' : 'Save Analysis'}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        )}
      </div>

      {/* Methodology Info */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
        <h4 className="font-bold text-blue-800 mb-2">💡 5-Why Methodology</h4>
        <p className="text-sm text-blue-700">
          Start with the problem statement, then ask "Why did this happen?" for each answer. 
          Continue until you reach the fundamental root cause (usually 5 times, but can be more or less). 
          The goal is to identify systemic issues rather than symptoms.
        </p>
      </div>
    </div>
  );
};

export default FiveWhyAnalysis;
